import React, { useEffect, useState } from "react";
import { Plus, Loader2, Magnet as MagnetIcon, Link2, Check, X as XIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  magnet: "",
  info_hash: "",
  name: "",
  source: "Manual",
  tmdb_id: "",
  media_type: "movie",
  season: "",
  episode: "",
  size: "",
  size_bytes: "",
  seeders: 0,
  leechers: 0,
};

const TMDB_URL = /themoviedb\.org\/(movie|tv)\/(\d+)(?:[^/]*)?(?:\/season\/(\d+)(?:\/episode\/(\d+))?)?/i;

export default function AdminAddTorrent({ onAdded }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [tmdbUrl, setTmdbUrl] = useState("");
  const [lookup, setLookup] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, ok: 0, fail: 0 });
  const [bulkResults, setBulkResults] = useState([]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Honor URL prefill params (e.g. `/admin?prefill_tmdb=414906&prefill_type=movie`)
  // so the "Add to cloud now" deep-link from a movie modal lands ready-to-fill.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const tmdb = params.get("prefill_tmdb");
    const type = params.get("prefill_type");
    if (!tmdb) return;
    const url = `https://www.themoviedb.org/${type === "tv" ? "tv" : "movie"}/${tmdb}`;
    setTmdbUrl(url);
    autofillFromTmdb(url);
    // Clean the params out of the URL so a manual refresh doesn't re-trigger.
    try {
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete("prefill_tmdb");
      cleaned.searchParams.delete("prefill_type");
      window.history.replaceState({}, "", cleaned.toString());
    } catch { /* no-op */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const autofillFromTmdb = async (raw) => {
    const m = (raw || "").match(TMDB_URL);
    if (!m) {
      toast.error("Not a recognised TMDB URL");
      return;
    }
    const [, media_type, idStr, seasonStr, episodeStr] = m;
    setLookup(true);
    try {
      const { data } = await api.get(`/details/${media_type}/${idStr}`);
      const titleStr = [data.title, data.year && `(${data.year})`].filter(Boolean).join(" ");
      setForm((f) => ({
        ...f,
        tmdb_id: String(data.id),
        media_type,
        name: titleStr || f.name,
        season: seasonStr || f.season,
        episode: episodeStr || f.episode,
      }));
      toast.success(`Loaded: ${titleStr}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load TMDB details");
    } finally {
      setLookup(false);
    }
  };

  const parseMagnetName = (line) => {
    const dn = line.match(/[?&]dn=([^&]+)/);
    if (dn) try { return decodeURIComponent(dn[1].replace(/\+/g, " ")); } catch { return dn[1]; }
    const hashOnly = line.match(/btih:([a-fA-F0-9]{40})/);
    return hashOnly ? `Untitled · ${hashOnly[1].slice(0, 8)}` : line.slice(0, 80);
  };

  const bulkImport = async () => {
    const lines = bulkText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.toLowerCase().startsWith("magnet:?") || /[a-f0-9]{40}/i.test(l));
    if (lines.length === 0) {
      toast.error("No magnet links or hashes detected");
      return;
    }
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: lines.length, ok: 0, fail: 0 });
    let ok = 0, fail = 0;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const isMagnet = line.toLowerCase().startsWith("magnet:?");
      const hashOnly = line.match(/^[a-fA-F0-9]{40}$/);
      try {
        await api.post("/admin/torrents/add", {
          magnet: isMagnet ? line : undefined,
          info_hash: hashOnly ? line : undefined,
          name: parseMagnetName(line),
          source: form.source.trim() || "Bulk",
          tmdb_id: form.tmdb_id ? parseInt(form.tmdb_id, 10) : undefined,
          media_type: form.media_type,
          season: form.season ? parseInt(form.season, 10) : undefined,
          episode: form.episode ? parseInt(form.episode, 10) : undefined,
        });
        ok++;
      } catch {
        fail++;
      }
      setBulkProgress({ done: i + 1, total: lines.length, ok, fail });
    }
    setBulkBusy(false);
    toast.success(`Imported ${ok}${fail ? ` (${fail} failed/duplicate)` : ""}`);
    if (ok > 0) {
      setBulkText("");
      onAdded?.();
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        magnet: form.magnet.trim() || undefined,
        info_hash: form.info_hash.trim() || undefined,
        name: form.name.trim(),
        source: form.source.trim() || "Manual",
        tmdb_id: form.tmdb_id ? parseInt(form.tmdb_id, 10) : undefined,
        media_type: form.media_type,
        season: form.season ? parseInt(form.season, 10) : undefined,
        episode: form.episode ? parseInt(form.episode, 10) : undefined,
        size: form.size.trim() || undefined,
        size_bytes: form.size_bytes ? parseInt(form.size_bytes, 10) : 0,
        seeders: parseInt(form.seeders || 0, 10),
        leechers: parseInt(form.leechers || 0, 10),
      };
      const { data } = await api.post("/admin/torrents/add", payload);
      toast.success(`Added: ${data.row.name}`);
      setForm(EMPTY);
      setTmdbUrl("");
      onAdded?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-10" data-testid="admin-add-torrent-section">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Plus className="w-5 h-5 text-emerald-400" />
          Add Torrent
        </h2>
        <span className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] font-semibold">
          attach to a TMDB title or just to the global index
        </span>
      </div>
      <form
        onSubmit={submit}
        className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5 grid md:grid-cols-2 gap-3"
        data-testid="admin-add-torrent-form"
      >
        <div className="md:col-span-2">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1 flex items-center gap-1">
            <Link2 className="w-3 h-3" /> TMDB URL — paste to auto-fill name + IDs
          </div>
          <div className="flex gap-2">
            <input
              value={tmdbUrl}
              onChange={(e) => setTmdbUrl(e.target.value)}
              onBlur={() => tmdbUrl && autofillFromTmdb(tmdbUrl)}
              data-testid="add-torrent-tmdb-url"
              placeholder="https://www.themoviedb.org/movie/27205-inception"
              className="flex-1 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] font-mono-tt"
            />
            <button
              type="button"
              onClick={() => autofillFromTmdb(tmdbUrl)}
              disabled={!tmdbUrl || lookup}
              data-testid="add-torrent-tmdb-lookup"
              className="bg-[var(--tt-surface-2)] hover:bg-[#222] border border-[var(--tt-border)] text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-1 disabled:opacity-50"
            >
              {lookup ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
              Fetch
            </button>
          </div>
        </div>

        <label className="md:col-span-2 block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1 flex items-center gap-1">
            <MagnetIcon className="w-3 h-3" /> Magnet URL (preferred)
          </div>
          <input
            value={form.magnet}
            onChange={(e) => update("magnet", e.target.value)}
            data-testid="add-torrent-magnet"
            placeholder="magnet:?xt=urn:btih:..."
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] font-mono-tt"
          />
        </label>
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Info hash (if no magnet)</div>
          <input
            value={form.info_hash}
            onChange={(e) => update("info_hash", e.target.value)}
            data-testid="add-torrent-hash"
            placeholder="40-hex characters"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] font-mono-tt"
          />
        </label>
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Source label</div>
          <input
            value={form.source}
            onChange={(e) => update("source", e.target.value)}
            data-testid="add-torrent-source"
            placeholder="Manual / YTS / EZTV / TPB ..."
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
          />
        </label>
        <label className="md:col-span-2 block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Torrent name</div>
          <input
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            required
            data-testid="add-torrent-name"
            placeholder="Inception (2010) 2160p UHD BluRay DV HDR HEVC"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
          />
        </label>
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">TMDB ID</div>
          <input
            value={form.tmdb_id}
            onChange={(e) => update("tmdb_id", e.target.value)}
            data-testid="add-torrent-tmdb"
            type="number"
            placeholder="27205"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
          />
        </label>
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Media type</div>
          <select
            value={form.media_type}
            onChange={(e) => update("media_type", e.target.value)}
            data-testid="add-torrent-media-type"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
          >
            <option value="movie">Movie</option>
            <option value="tv">TV</option>
          </select>
        </label>
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Season (TV)</div>
          <input
            value={form.season}
            onChange={(e) => update("season", e.target.value)}
            data-testid="add-torrent-season"
            type="number" min="1" max="50"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
          />
        </label>
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Episode (TV)</div>
          <input
            value={form.episode}
            onChange={(e) => update("episode", e.target.value)}
            data-testid="add-torrent-episode"
            type="number" min="1" max="500"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
          />
        </label>
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Size (text e.g. 2.1 GB)</div>
          <input
            value={form.size}
            onChange={(e) => update("size", e.target.value)}
            data-testid="add-torrent-size"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
          />
        </label>
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Size in bytes</div>
          <input
            value={form.size_bytes}
            onChange={(e) => update("size_bytes", e.target.value)}
            data-testid="add-torrent-size-bytes"
            type="number"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
          />
        </label>
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Seeders</div>
          <input
            value={form.seeders}
            onChange={(e) => update("seeders", e.target.value)}
            data-testid="add-torrent-seeders"
            type="number" min="0"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
          />
        </label>
        <label className="block">
          <div className="text-xs text-[var(--tt-text-muted)] mb-1">Leechers</div>
          <input
            value={form.leechers}
            onChange={(e) => update("leechers", e.target.value)}
            data-testid="add-torrent-leechers"
            type="number" min="0"
            className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
          />
        </label>
        <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => { setForm(EMPTY); setTmdbUrl(""); }}
            className="text-xs text-[var(--tt-text-muted)] hover:text-white"
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={busy || !form.name || (!form.magnet && !form.info_hash)}
            data-testid="add-torrent-submit"
            className="bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-sm font-semibold px-5 py-2 rounded-full flex items-center gap-2 disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add torrent
          </button>
        </div>
      </form>

      {/* Bulk import */}
      <div className="mt-4 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden" data-testid="bulk-import-section">
        <button
          type="button"
          onClick={() => setBulkOpen((o) => !o)}
          data-testid="bulk-import-toggle"
          className="w-full px-5 py-3 flex items-center gap-2 text-left hover:bg-white/[0.03] transition-colors"
        >
          <span className="text-sm font-display font-bold flex-1">
            Bulk import — paste many magnet links / hashes at once
          </span>
          <span className="text-xs text-[var(--tt-primary)] font-semibold">{bulkOpen ? "Hide" : "Show"}</span>
        </button>
        {bulkOpen && (
          <div className="border-t border-[var(--tt-border)] p-5 space-y-3">
            <div className="text-[11px] text-[var(--tt-text-muted)]">
              One magnet link (or bare 40-hex info_hash) per line. They will all be attached to the same TMDB title / season / episode and source label currently set above.
              {form.tmdb_id ? (
                <span className="ml-1 text-emerald-400">Attaching to TMDB {form.tmdb_id} · {form.media_type}{form.season ? ` · S${form.season}` : ""}{form.episode ? `E${form.episode}` : ""}</span>
              ) : (
                <span className="ml-1 text-amber-400">No TMDB title set — entries will go to the global index only.</span>
              )}
            </div>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={6}
              data-testid="bulk-import-textarea"
              placeholder={"magnet:?xt=urn:btih:...\nmagnet:?xt=urn:btih:...\n43e3691dc6f4172841e32b25b349e2b7a980b9c5"}
              className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-xs outline-none focus:border-[var(--tt-primary)] font-mono-tt resize-y"
            />
            <div className="flex items-center gap-3">
              {bulkBusy && (
                <span className="text-xs text-[var(--tt-text-muted)] font-mono-tt" data-testid="bulk-import-progress">
                  {bulkProgress.done}/{bulkProgress.total} · {bulkProgress.ok} ok · {bulkProgress.fail} failed
                </span>
              )}
              <button
                type="button"
                onClick={bulkImport}
                disabled={bulkBusy || !bulkText.trim()}
                data-testid="bulk-import-submit"
                className="ml-auto bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-sm font-semibold px-5 py-2 rounded-full flex items-center gap-2 disabled:opacity-50"
              >
                {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Import all
              </button>
            </div>

            {bulkResults.length > 0 && (
              <ul
                data-testid="bulk-import-results"
                className="mt-2 max-h-56 overflow-y-auto divide-y divide-[var(--tt-border)] border border-[var(--tt-border)] rounded-lg bg-[var(--tt-surface-2)]"
              >
                {bulkResults.map((r, i) => (
                  <li
                    key={i}
                    data-testid={`bulk-import-result-${i}`}
                    className="px-3 py-2 flex items-start gap-2 text-xs"
                  >
                    {r.ok ? (
                      <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                    ) : (
                      <XIcon className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate" title={r.name}>{r.name}</div>
                      {r.hash && (
                        <div className="font-mono-tt text-[9px] text-[var(--tt-text-faint)] truncate">{r.hash}</div>
                      )}
                      {!r.ok && r.reason && (
                        <div className="text-[10px] text-red-300 mt-0.5">{r.reason}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
